import {
  lineasInsumoEscaladosRecetaApi,
  formatearCantidadReceta4Dec,
} from '../data/mockSalidasProduccion';
import { resolveFabricarSubrecetaDesdePt } from '../data/mockRecetasClassification';
import { parseRecetaOutputQuantity } from './recipeQuantityLimits';

/** Redondeo de subtotales de línea (4 decimales, alineado con C.unit). */
export function roundSubtotal4(value) {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
  const x = Number.isFinite(n) ? n : 0;
  return Math.round(x * 10000) / 10000;
}

/** Cantidad elaborada numérica desde el ítem de guía. */
export function parseGuiaItemQuantity(value) {
  if (value === '' || value == null) return 0;
  const n = parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Ítems de guía con cantidad elaborada > 0. */
export function getGuiaItemsWithQuantity(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      ...row,
      qty: parseGuiaItemQuantity(row.quantity),
    }))
    .filter((row) => row.qty > 0);
}

/**
 * Resumen de insumos agregados (antes de kárdex).
 * @param {Array} itemsWithQty filas con receta_plato_id y qty
 * @param {Map<string, object>} recetasFullById
 */
export function buildResumenInsumosGuia(itemsWithQty, recetasFullById) {
  const map = new Map();
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  for (const item of itemsWithQty || []) {
    const idStr = item.receta_plato_id != null ? String(item.receta_plato_id) : null;
    if (!idStr) continue;
    const receta = m.get(idStr);
    if (!receta) continue;
    const ings = lineasInsumoEscaladosRecetaApi(receta, item.qty);
    for (const row of ings) {
      const key = row.product_id != null ? `id:${row.product_id}|||${row.unit}` : `${row.product}|||${row.unit}`;
      const prev = map.get(key) || {
        product: row.product,
        product_id: row.product_id ?? null,
        unit: row.unit,
        quantity: 0,
        quantity_net: 0,
        quantity_merma: 0,
        subtotal: 0,
      };
      prev.quantity = Math.round((prev.quantity + row.quantity) * 10000) / 10000;
      prev.quantity_net = Math.round((Number(prev.quantity_net || 0) + Number((row.quantity_net ?? row.quantity) || 0)) * 10000) / 10000;
      prev.quantity_merma = Math.round((Number(prev.quantity_merma || 0) + Number(row.quantity_merma || 0)) * 10000) / 10000;
      prev.subtotal = roundSubtotal4(prev.subtotal + row.subtotal);
      map.set(key, prev);
    }
  }
  const filas = Array.from(map.values()).sort((a, b) => a.product.localeCompare(b.product, 'es'));
  const totalCosto = roundSubtotal4(filas.reduce((s, r) => s + (Number(r.subtotal) || 0), 0));
  return { filas, totalCosto: Number.isFinite(totalCosto) ? totalCosto : 0 };
}

/** Aplica costos de kárdex al resumen de insumos. */
export function applyKardexToInsumoResumen(resumenInsumos, stockCostByProduct, productUnitById = {}) {
  const grouped = new Map();
  (resumenInsumos?.filas || []).forEach((row) => {
    const pid = row.product_id != null ? String(row.product_id) : null;
    const canonicalUnit = pid ? (productUnitById[pid] || row.unit || '') : (row.unit || '');
    const key = pid ? `id:${pid}|||${canonicalUnit}` : `name:${row.product}|||${canonicalUnit}`;
    const prev = grouped.get(key) || {
      ...row,
      unit: canonicalUnit || row.unit,
      quantity: 0,
      quantity_net: 0,
      quantity_merma: 0,
    };
    prev.quantity = Math.round((Number(prev.quantity || 0) + Number(row.quantity || 0)) * 10000) / 10000;
    prev.quantity_net = Math.round(
      (Number(prev.quantity_net || 0) + Number((row.quantity_net ?? row.quantity) || 0)) * 10000,
    ) / 10000;
    prev.quantity_merma = Math.round(
      (Number(prev.quantity_merma || 0) + Number(row.quantity_merma || 0)) * 10000,
    ) / 10000;
    grouped.set(key, prev);
  });
  const filas = Array.from(grouped.values()).map((row) => {
    const pid = row.product_id != null ? String(row.product_id) : null;
    const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
    const cUnit = Number(cached?.average_cost ?? 0);
    const stock = cached?.total_stock != null ? Number(cached.total_stock) : null;
    const subtotal = roundSubtotal4(Number(row.quantity || 0) * (Number.isFinite(cUnit) ? cUnit : 0));
    return {
      ...row,
      costoUnitarioKardex: Number.isFinite(cUnit) ? cUnit : 0,
      stockKardex: Number.isFinite(stock) ? stock : null,
      subtotalKardex: subtotal,
    };
  });
  const totalCosto = roundSubtotal4(filas.reduce((s, r) => s + (Number(r.subtotalKardex) || 0), 0));
  return { filas, totalCosto };
}

/** Clasifica un producto de receta como subproducto (tiene ficha PEP/PT) vs insumo simple. */
export function isSubproductoProduct(productId, productTipoById) {
  if (productId == null) return false;
  const meta = productTipoById?.[String(productId)];
  if (!meta) return false;
  if (meta.output_product_type === 'producto_en_proceso' || meta.output_product_type === 'producto_terminado') {
    return true;
  }
  return Boolean(meta.linked_to_recipe);
}

function accumulateInsumoRow(map, row) {
  const pid = row.product_id != null ? String(row.product_id) : null;
  const unit = row.unit || '';
  const key = pid ? `id:${pid}|||${unit}` : `name:${row.product}|||${unit}`;
  const prev = map.get(key) || {
    product: row.product,
    product_id: row.product_id ?? null,
    unit,
    quantity: 0,
    quantity_net: 0,
    quantity_merma: 0,
    subtotal: 0,
  };
  prev.quantity = Math.round((Number(prev.quantity || 0) + Number(row.quantity || 0)) * 10000) / 10000;
  prev.quantity_net = Math.round(
    (Number(prev.quantity_net || 0) + Number((row.quantity_net ?? row.quantity) || 0)) * 10000,
  ) / 10000;
  prev.quantity_merma = Math.round(
    (Number(prev.quantity_merma || 0) + Number(row.quantity_merma || 0)) * 10000,
  ) / 10000;
  prev.subtotal = roundSubtotal4(Number(prev.subtotal || 0) + Number(row.subtotal || 0));
  map.set(key, prev);
  return key;
}

function pushDesglose(desgloseMap, key, entry) {
  if (!desgloseMap.has(key)) desgloseMap.set(key, []);
  desgloseMap.get(key).push(entry);
}

/**
 * Cuando se fabrican PEP desde PT: separa insumos hoja de subproductos y costeá los PEP.
 * @returns {{
 *   hasPep: boolean,
 *   insumosPep: { filas: array, totalCosto: number },
 *   insumosAgregados: { filas: array, totalCosto: number },
 *   pepUnitCostByProductId: Record<string, number>,
 *   desglosePep: Map,
 *   desgloseAgregados: Map,
 * }}
 */
export function buildExplosionPepAnalisis({
  itemsWithQty,
  recetasFullById,
  pepRecetasByProductId,
  stockCostByProduct,
  productUnitById = {},
  productTipoById = {},
  globalFabricarSubrecetaDefault = false,
}) {
  const empty = {
    hasPep: false,
    insumosPep: { filas: [], totalCosto: 0 },
    insumosAgregados: { filas: [], totalCosto: 0 },
    resumenSubproductos: { rows: [], costTotal: 0 },
    resumenCoproductosPep: { rows: [], costTotal: 0 },
    pepUnitCostByProductId: {},
    desglosePep: new Map(),
    desgloseAgregados: new Map(),
  };
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  const pepMap = pepRecetasByProductId instanceof Map ? pepRecetasByProductId : new Map();
  if (!pepMap.size) return empty;

  const pepLeavesRaw = new Map();
  const ptDirectRaw = new Map();
  const pepNeedsRaw = new Map();
  const fabricatedPepByRecetaId = new Map();
  const desglosePep = new Map();
  const desgloseAgregados = new Map();

  let foundPep = false;

  for (const item of itemsWithQty || []) {
    const idStr = item.receta_plato_id != null ? String(item.receta_plato_id) : null;
    if (!idStr) continue;
    const receta = m.get(idStr);
    if (!receta) continue;
    const ings = lineasInsumoEscaladosRecetaApi(receta, item.qty);
    const recetaLabel = item.receta_name || receta.name || '—';
    const classification = item.classification || '—';

    for (const row of ings) {
      const pid = row.product_id != null ? String(row.product_id) : null;
      const pepReceta = pid ? pepMap.get(pid) : null;
      const isPep = Boolean(pepReceta) && isSubproductoProduct(row.product_id, productTipoById);
      const fabricarSubreceta = isPep
        ? resolveFabricarSubrecetaDesdePt(
            pepReceta?.fabricar_subreceta_desde_pt,
            globalFabricarSubrecetaDefault,
          )
        : false;

      if (isPep && fabricarSubreceta) {
        foundPep = true;
        const pepPid = pid;
        const recetaKey = pepReceta?.id != null ? String(pepReceta.id) : null;
        if (recetaKey) {
          const prev = fabricatedPepByRecetaId.get(recetaKey) || {
            receta: pepReceta,
            primaryQty: 0,
          };
          prev.primaryQty = Math.round((Number(prev.primaryQty || 0) + Number(row.quantity || 0)) * 10000) / 10000;
          fabricatedPepByRecetaId.set(recetaKey, prev);
        }
        const pepCanonicalUnit = pepPid
          ? (productUnitById[pepPid] || row.unit || '')
          : (row.unit || '');
        const pepNeedRow = { ...row, unit: pepCanonicalUnit || row.unit };
        const needKey = accumulateInsumoRow(pepNeedsRaw, pepNeedRow);
        pushDesglose(desgloseAgregados, needKey, {
          receta: recetaLabel,
          classification,
          quantity: Number(row.quantity || 0),
          unit: pepCanonicalUnit || row.unit,
          cUnit: 0,
          subtotal: 0,
          _pendingPepCost: true,
          _productId: pid,
        });

        const leaves = lineasInsumoEscaladosRecetaApi(pepReceta, row.quantity);
        const pepName = pepReceta.name || row.product || 'Subproducto';
        for (const leaf of leaves) {
          const leafPid = leaf.product_id != null ? String(leaf.product_id) : null;
          const canonicalUnit = leafPid
            ? (productUnitById[leafPid] || leaf.unit || '')
            : (leaf.unit || '');
          const leafCanon = { ...leaf, unit: canonicalUnit || leaf.unit };
          const leafKey = accumulateInsumoRow(pepLeavesRaw, leafCanon);
          const cached = leafPid
            ? (stockCostByProduct?.[leafPid] || stockCostByProduct?.[leaf.product_id])
            : null;
          const cUnit = Number(cached?.average_cost ?? leaf.precioUnitario ?? 0);
          const safe = Number.isFinite(cUnit) ? cUnit : 0;
          const subtotal = roundSubtotal4(Number(leaf.quantity || 0) * safe);
          pushDesglose(desglosePep, leafKey, {
            receta: pepName,
            classification: 'Producto en proceso',
            quantity: Number(leaf.quantity || 0),
            unit: canonicalUnit || leaf.unit,
            cUnit: safe,
            subtotal,
          });
        }
      } else {
        const directKey = accumulateInsumoRow(ptDirectRaw, row);
        const leafPid = row.product_id != null ? String(row.product_id) : null;
        const cached = leafPid
          ? (stockCostByProduct?.[leafPid] || stockCostByProduct?.[row.product_id])
          : null;
        const cUnit = Number(cached?.average_cost ?? row.precioUnitario ?? 0);
        const safe = Number.isFinite(cUnit) ? cUnit : 0;
        const subtotal = roundSubtotal4(Number(row.quantity || 0) * safe);
        pushDesglose(desgloseAgregados, directKey, {
          receta: recetaLabel,
          classification,
          quantity: Number(row.quantity || 0),
          unit: row.unit,
          cUnit: safe,
          subtotal,
        });
      }
    }
  }

  if (!foundPep) return empty;

  const insumosPep = applyKardexToInsumoResumen(
    { filas: Array.from(pepLeavesRaw.values()) },
    stockCostByProduct,
    productUnitById,
  );

  for (const [k, list] of desglosePep.entries()) {
    list.sort((a, b) => String(a.receta).localeCompare(String(b.receta), 'es'));
    desglosePep.set(k, list);
  }

  const pepUnitCostByProductId = {};
  for (const need of pepNeedsRaw.values()) {
    const pid = need.product_id != null ? String(need.product_id) : null;
    if (!pid) continue;
    const pepReceta = pepMap.get(pid);
    if (!pepReceta) continue;
    const leaves = lineasInsumoEscaladosRecetaApi(pepReceta, need.quantity);
    const totalCost = Math.round(
      leaves.reduce((acc, leaf) => {
        const leafPid = leaf.product_id != null ? String(leaf.product_id) : null;
        const cached = leafPid
          ? (stockCostByProduct?.[leafPid] || stockCostByProduct?.[leaf.product_id])
          : null;
        const cUnit = Number(cached?.average_cost ?? leaf.precioUnitario ?? 0);
        const safe = Number.isFinite(cUnit) ? cUnit : 0;
        return acc + Number(leaf.quantity || 0) * safe;
      }, 0) * 100,
    ) / 100;
    const qty = Number(need.quantity || 0);
    pepUnitCostByProductId[pid] = qty > 0 ? Math.round((totalCost / qty) * 10000) / 10000 : 0;
  }

  // Actualizar cUnit/subtotal en desglose de subproductos
  for (const [key, list] of desgloseAgregados.entries()) {
    for (const entry of list) {
      if (entry._pendingPepCost && entry._productId) {
        const cUnit = pepUnitCostByProductId[entry._productId] ?? 0;
        entry.cUnit = cUnit;
        entry.subtotal = roundSubtotal4(Number(entry.quantity || 0) * cUnit);
        delete entry._pendingPepCost;
        delete entry._productId;
      }
    }
    list.sort((a, b) => String(a.receta).localeCompare(String(b.receta), 'es'));
    desgloseAgregados.set(key, list);
  }

  const directKardex = applyKardexToInsumoResumen(
    { filas: Array.from(ptDirectRaw.values()) },
    stockCostByProduct,
    productUnitById,
  );

  const pepFilas = Array.from(pepNeedsRaw.values()).map((need) => {
    const pid = need.product_id != null ? String(need.product_id) : null;
    const canonicalUnit = pid ? (productUnitById[pid] || need.unit || '') : (need.unit || '');
    const cUnit = pid ? (pepUnitCostByProductId[pid] ?? 0) : 0;
    const qty = Number(need.quantity || 0);
    const cached = pid
      ? (stockCostByProduct?.[pid] || stockCostByProduct?.[need.product_id])
      : null;
    const stock = cached?.total_stock != null ? Number(cached.total_stock) : null;
    return {
      product: need.product,
      product_id: need.product_id,
      unit: canonicalUnit || need.unit,
      quantity: qty,
      costoUnitarioKardex: cUnit,
      stockKardex: Number.isFinite(stock) ? stock : 0,
      subtotalKardex: roundSubtotal4(qty * cUnit),
      esSubproductoCosteado: true,
    };
  });

  const resumenSubproductosRows = pepFilas
    .map((row) => {
      const pid = row.product_id != null ? String(row.product_id) : null;
      const pepReceta = pid ? pepMap.get(pid) : null;
      const family = pepReceta?.family;
      const classification = family
        ? [family.subcategory?.category?.name, family.subcategory?.name, family.name]
            .filter(Boolean)
            .join(' › ') || 'Producto en proceso'
        : 'Producto en proceso';
      return {
        product_id: row.product_id,
        product_name: pepReceta?.name || row.product,
        receta_code: pepReceta?.code || '',
        classification,
        quantity: row.quantity,
        unit_of_measure: row.unit,
        unitPrice: row.costoUnitarioKardex,
        totalCost: row.subtotalKardex,
      };
    })
    .sort((a, b) => String(a.product_name).localeCompare(String(b.product_name), 'es'));
  const resumenSubproductosCostTotal =
    roundSubtotal4(resumenSubproductosRows.reduce((s, r) => s + (Number(r.totalCost) || 0), 0));

  const resumenCoproductosPep = buildCoproductosMultiSalidaPepFabricacion(
    fabricatedPepByRecetaId,
    pepUnitCostByProductId,
    productUnitById,
  );

  const agregadosFilas = [...directKardex.filas, ...pepFilas].sort((a, b) =>
    String(a.product).localeCompare(String(b.product), 'es'),
  );
  const agregadosTotal =
    roundSubtotal4(agregadosFilas.reduce((s, r) => s + (Number(r.subtotalKardex) || 0), 0));

  // Normalizar keys de desglose agregados a unidades canónicas
  const desgloseAgregadosCanon = new Map();
  for (const [key, list] of desgloseAgregados.entries()) {
    let canonKey = key;
    const match = /^id:(\d+)\|\|\|/.exec(key);
    if (match) {
      const pid = match[1];
      const unitFromList = list[0]?.unit;
      const canonicalUnit = productUnitById[pid] || unitFromList || '';
      canonKey = `id:${pid}|||${canonicalUnit}`;
    }
    const prev = desgloseAgregadosCanon.get(canonKey) || [];
    desgloseAgregadosCanon.set(canonKey, prev.concat(list));
  }
  for (const [k, list] of desgloseAgregadosCanon.entries()) {
    list.sort((a, b) => String(a.receta).localeCompare(String(b.receta), 'es'));
    desgloseAgregadosCanon.set(k, list);
  }

  return {
    hasPep: true,
    insumosPep,
    insumosAgregados: { filas: agregadosFilas, totalCosto: agregadosTotal },
    resumenSubproductos: { rows: resumenSubproductosRows, costTotal: resumenSubproductosCostTotal },
    resumenCoproductosPep,
    pepUnitCostByProductId,
    desglosePep,
    desgloseAgregados: desgloseAgregadosCanon,
  };
}

/**
 * Co-productos de recetas PEP con multi-salida al fabricarlas automáticamente (ej. tierra del keke).
 */
export function buildCoproductosMultiSalidaPepFabricacion(
  fabricatedPepByRecetaId,
  pepUnitCostByProductId = {},
  productUnitById = {},
) {
  const map = new Map();
  const batches = fabricatedPepByRecetaId instanceof Map ? fabricatedPepByRecetaId : new Map();

  for (const { receta, primaryQty } of batches.values()) {
    if (!receta?.multi_output_enabled || !Array.isArray(receta.outputs) || receta.outputs.length < 2) {
      continue;
    }
    const primaryOutputQty = parseRecetaOutputQuantity(receta);
    const factor = primaryOutputQty > 0 ? Number(primaryQty || 0) / primaryOutputQty : Number(primaryQty || 0);
    const primaryPid = receta.product_id != null ? String(receta.product_id) : null;
    const unitCost = primaryPid ? Number(pepUnitCostByProductId[primaryPid] ?? 0) : 0;
    const batchCost = roundSubtotal4(Number(primaryQty || 0) * (Number.isFinite(unitCost) ? unitCost : 0));

    const outputs = [...receta.outputs].sort(
      (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0),
    );
    for (const out of outputs) {
      if (out.is_primary) continue;
      const productId = out.product_id ?? out.finished_product?.id ?? null;
      if (productId == null) continue;
      const outBaseQty = Number(out.output_quantity || 0);
      if (!(outBaseQty > 0) || !(factor > 0)) continue;
      const qty = Math.round(outBaseQty * factor * 10000) / 10000;
      if (!(qty > 0)) continue;
      const sharePct = Number(out.cost_share_pct);
      const allocated =
        Number.isFinite(sharePct) && sharePct > 0 ? roundSubtotal4(batchCost * (sharePct / 100)) : 0;
      const unitPrice = qty > 0 ? Math.round((allocated / qty) * 10000) / 10000 : 0;
      const pid = String(productId);
      const unit =
        productUnitById[pid] ||
        out.finished_product?.unit_of_measure ||
        out.output_unit_of_measure ||
        'UN';
      const key = `${pid}|||${unit}`;
      const prev = map.get(key) || {
        product_id: Number(productId),
        product_name: out.name || out.finished_product?.name || `Producto #${productId}`,
        receta_name: receta.name || '—',
        receta_code: receta.code || '',
        unit_of_measure: unit,
        quantity: 0,
        unitPrice: 0,
        totalCost: 0,
        cost_share_pct: Number.isFinite(sharePct) ? sharePct : null,
        source_pep_name: receta.name || '—',
      };
      prev.quantity = Math.round((Number(prev.quantity || 0) + qty) * 10000) / 10000;
      prev.totalCost = roundSubtotal4(Number(prev.totalCost || 0) + allocated);
      prev.unitPrice = prev.quantity > 0 ? Math.round((prev.totalCost / prev.quantity) * 10000) / 10000 : unitPrice;
      map.set(key, prev);
    }
  }

  const rows = Array.from(map.values()).sort((a, b) =>
    String(a.product_name).localeCompare(String(b.product_name), 'es'),
  );
  const costTotal = roundSubtotal4(rows.reduce((s, r) => s + (Number(r.totalCost) || 0), 0));
  return { rows, costTotal };
}

/**
 * Aplica co-productos PEP como abono en insumos agregados: reduce despacho de stock y suma su costo.
 */
export function applyCoproductosToInsumoResumen(resumenInsumos, coproductosResumen) {
  const filas = (resumenInsumos?.filas || []).map((row) => ({ ...row }));
  const coproductRemaining = new Map();

  for (const cp of coproductosResumen?.rows || []) {
    if (cp?.product_id == null) continue;
    const pid = String(cp.product_id);
    const prev = coproductRemaining.get(pid) || { quantity: 0, totalCost: 0, unitPrice: 0 };
    const qty = Number(cp.quantity || 0);
    const total = roundSubtotal4(Number(prev.totalCost || 0) + Number(cp.totalCost || 0));
    const nextQty = Math.round((Number(prev.quantity || 0) + qty) * 10000) / 10000;
    coproductRemaining.set(pid, {
      quantity: nextQty,
      totalCost: total,
      unitPrice: nextQty > 0 ? Math.round((total / nextQty) * 10000) / 10000 : Number(cp.unitPrice || 0),
    });
  }

  for (const row of filas) {
    if (row.esSubproductoCosteado || row.product_id == null) continue;
    const pid = String(row.product_id);
    const cp = coproductRemaining.get(pid);
    if (!cp || !(cp.quantity > 0)) continue;

    const bruto = Number(row.quantity || 0);
    const creditQty = Math.min(bruto, cp.quantity);
    if (!(creditQty > 0)) continue;

    const cpUnit = Number(cp.unitPrice || 0);
    const kardexUnit = Number(row.costoUnitarioKardex || 0);
    const creditCost = roundSubtotal4(creditQty * cpUnit);
    const despachoQty = Math.round((bruto - creditQty) * 10000) / 10000;
    const despachoCost = roundSubtotal4(despachoQty * kardexUnit);

    row.quantity_bruto_receta = bruto;
    row.cantidadCoproducto = creditQty;
    row.cantidadDespacho = despachoQty;
    row.costoUnitarioDespacho = kardexUnit;
    row.costoUnitarioKardex = bruto > 0 ? roundSubtotal4((creditCost + despachoCost) / bruto) : kardexUnit;
    row.subtotalKardex = roundSubtotal4(creditCost + despachoCost);
    row.coproductoAplicado = true;

    const cpLeftQty = Math.round((cp.quantity - creditQty) * 10000) / 10000;
    const cpLeftCost = roundSubtotal4(Number(cp.totalCost || 0) - creditCost);
    coproductRemaining.set(pid, {
      quantity: cpLeftQty,
      totalCost: cpLeftCost,
      unitPrice: cpLeftQty > 0 ? Math.round((cpLeftCost / cpLeftQty) * 10000) / 10000 : cpUnit,
    });
  }

  let orphanCoproductCost = 0;
  for (const cp of coproductRemaining.values()) {
    orphanCoproductCost = roundSubtotal4(orphanCoproductCost + Number(cp.totalCost || 0));
  }

  const totalCosto = roundSubtotal4(
    filas.reduce((s, r) => s + Number(r.subtotalKardex || 0), 0) + orphanCoproductCost,
  );

  return { filas, totalCosto };
}

/** Cantidad del mismo producto que se generará en esta guía (co-productos, PEP, subproductos recuperados). */
export function buildGenerandoseByProductId({
  coproductosRows = [],
  subproductosPepRows = [],
  byproductRows = [],
} = {}) {
  const map = new Map();
  const add = (productId, qty) => {
    if (productId == null || !(Number(qty) > 0)) return;
    const k = String(productId);
    map.set(k, Math.round(((map.get(k) || 0) + Number(qty)) * 10000) / 10000);
  };
  for (const r of coproductosRows) add(r.product_id, r.quantity);
  for (const r of subproductosPepRows) add(r.product_id, r.quantity);
  for (const r of byproductRows) add(r.product_id, r.quantity);
  return map;
}

/** Stock kárdex + cantidad que se fabricará en la misma guía antes del consumo PT. */
export function stockDisponibleInsumoGuia(row, generandoseByProductId = new Map()) {
  const pid = row?.product_id != null ? String(row.product_id) : null;
  const generandose = Number(
    row?.cantidadCoproducto ?? (pid ? generandoseByProductId.get(pid) : 0) ?? 0,
  );
  const stock =
    row?.stockKardex == null || !Number.isFinite(Number(row.stockKardex))
      ? 0
      : Number(row.stockKardex);
  const disponible = Math.round((stock + generandose) * 10000) / 10000;
  const bruto = Number(row?.quantity || 0);
  const despacho =
    row?.cantidadDespacho != null
      ? Number(row.cantidadDespacho)
      : Math.max(0, Math.round((bruto - generandose) * 10000) / 10000);

  return { stock, generandose, disponible, bruto, despacho };
}

/** Bruto requerido mayor que stock kárdex + lo que se genera en la guía. */
export function insumoSinStockSuficiente(row, generandoseByProductId = new Map()) {
  if (!row || row.esSubproductoCosteado) return false;
  const { disponible, bruto } = stockDisponibleInsumoGuia(row, generandoseByProductId);
  if (!(bruto > 0)) return false;
  return disponible + 1e-9 < bruto;
}

/**
 * Costo por receta elaborada (costo promedio por unidad de salida).
 * Con multi-salida: genera una fila por producto y reparte el costo por cost_share_pct.
 * @param {Record<string, number>} [ingredientCostOverrides] product_id → costo unitario forzado (p. ej. PEP fabricado)
 */
export function buildResumenPlatosGuia(itemsWithQty, recetasFullById, stockCostByProduct, ingredientCostOverrides = {}) {
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  const rows = [];
  for (const item of itemsWithQty || []) {
    const idStr = item.receta_plato_id != null ? String(item.receta_plato_id) : null;
    if (!idStr) continue;
    const receta = m.get(idStr);
    if (!receta) continue;
    const qty = item.qty;
    const ings = lineasInsumoEscaladosRecetaApi(receta, qty);
    const totalCost = Math.round(
      ings.reduce((acc, ing) => {
        const pid = ing.product_id != null ? String(ing.product_id) : null;
        const override =
          pid != null && ingredientCostOverrides[pid] != null
            ? Number(ingredientCostOverrides[pid])
            : null;
        const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[ing.product_id]) : null;
        const cUnit =
          override != null && Number.isFinite(override)
            ? override
            : Number(cached?.average_cost ?? ing.precioUnitario ?? 0);
        const safe = Number.isFinite(cUnit) ? cUnit : 0;
        return acc + Number(ing.quantity || 0) * safe;
      }, 0) * 100,
    ) / 100;

    const outputs = resolveRecetaOutputs(receta, item);
    const primaryQtyBase = Number(outputs[0]?.output_quantity || item.output_quantity || 1) || 1;
    const factor = primaryQtyBase > 0 ? qty / primaryQtyBase : 0;

    outputs.forEach((out, idx) => {
      const outBaseQty = Number(out.output_quantity || 0) || 0;
      const outQty = Math.round(outBaseQty * factor * 10000) / 10000;
      const sharePct = Number(out.cost_share_pct);
      const share =
        Number.isFinite(sharePct) && sharePct > 0
          ? sharePct / 100
          : 1 / outputs.length;
      const allocated = Math.round(totalCost * share * 100) / 100;
      const unitPrice = outQty > 0 ? Math.round((allocated / outQty) * 10000) / 10000 : 0;
      let unitOfMeasure =
        out.finished_product?.unit_of_measure ||
        out.unit_of_measure ||
        out.output_unit_of_measure ||
        item.output_unit_of_measure ||
        'UN';
      if (String(unitOfMeasure).trim().toLowerCase() === 'unidad') {
        unitOfMeasure = 'UN';
      }
      const productId = out.product_id ?? out.finished_product?.id ?? null;
      const outName = out.name || receta.name || '—';
      const recetaLabel =
        outputs.length > 1
          ? `${item.receta_name || receta.name || '—'} → ${outName}`
          : item.receta_name || receta.name || '—';

      rows.push({
        receta_plato_id: item.receta_plato_id,
        output_id: out.id ?? null,
        is_primary_output: idx === 0 || Boolean(out.is_primary),
        product_id: productId != null ? Number(productId) : null,
        receta_name: recetaLabel,
        receta_code: item.receta_code || receta.code || '',
        classification: item.classification || '—',
        output_quantity: out.output_quantity,
        output_unit_of_measure: out.output_unit_of_measure || 'unidad',
        unit_of_measure: unitOfMeasure,
        quantity: outQty,
        totalCost: allocated,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        cost_share_pct: Number.isFinite(sharePct) ? sharePct : Math.round(share * 10000) / 100,
      });
    });
  }
  rows.sort((a, b) => String(a.receta_name).localeCompare(String(b.receta_name), 'es'));
  const qtyTotal = rows.reduce((acc, r) => acc + (Number(r.quantity) || 0), 0);
  const costTotal = Math.round(rows.reduce((acc, r) => acc + (Number(r.totalCost) || 0), 0) * 100) / 100;
  const avgUnit =
    qtyTotal > 0 ? Math.round((costTotal / qtyTotal) * 100) / 100 : 0;
  return { rows, qtyTotal, costTotal, avgUnit };
}

/** Normaliza salidas de una receta (multi o simple) para el resumen de guía. */
export function resolveRecetaOutputs(receta, item = {}) {
  if (receta?.multi_output_enabled && Array.isArray(receta.outputs) && receta.outputs.length > 0) {
    return [...receta.outputs].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  }
  const productId = receta?.product_id ?? receta?.finished_product?.id ?? receta?.finishedProduct?.id ?? null;
  return [
    {
      id: null,
      name: receta?.name || item.receta_name || '—',
      output_product_type: receta?.output_product_type || item.output_product_type || 'producto_terminado',
      output_quantity: receta?.output_quantity ?? item.output_quantity ?? 1,
      output_unit_of_measure:
        receta?.output_unit_of_measure || item.output_unit_of_measure || 'unidad',
      cost_share_pct: 100,
      is_primary: true,
      product_id: productId,
      finished_product: receta?.finished_product || receta?.finishedProduct || null,
    },
  ];
}

/**
 * Subproductos recuperados de insumos (ej. pepas de papaya = % del bruto despachado).
 * Entran a inventario junto con los elaborados (costo 0 por defecto).
 */
export function buildResumenByproductsFromInsumos(itemsWithQty, recetasFullById, productUnitById = {}) {
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  const map = new Map();
  for (const item of itemsWithQty || []) {
    const idStr = item.receta_plato_id != null ? String(item.receta_plato_id) : null;
    if (!idStr) continue;
    const receta = m.get(idStr);
    if (!receta) continue;
    const ings = lineasInsumoEscaladosRecetaApi(receta, item.qty);
    for (const row of ings) {
      if (!row.byproduct_product_id || !(Number(row.byproduct_quantity) > 0)) continue;
      const pid = String(row.byproduct_product_id);
      const unit = productUnitById[pid] || row.byproduct_unit || row.unit || 'UN';
      const key = `id:${pid}|||${unit}`;
      const prev = map.get(key) || {
        product_id: Number(row.byproduct_product_id),
        product: row.byproduct_name || `Producto #${row.byproduct_product_id}`,
        unit,
        quantity: 0,
        byproduct_pct: Number(row.byproduct_pct) || 0,
        source_product: row.product,
        unitPrice: 0,
        totalCost: 0,
        is_ingredient_byproduct: true,
      };
      prev.quantity = Math.round((Number(prev.quantity || 0) + Number(row.byproduct_quantity || 0)) * 10000) / 10000;
      if (!prev.source_product && row.product) prev.source_product = row.product;
      map.set(key, prev);
    }
  }
  const rows = Array.from(map.values()).sort((a, b) =>
    String(a.product).localeCompare(String(b.product), 'es'),
  );
  const qtyTotal = rows.reduce((acc, r) => acc + (Number(r.quantity) || 0), 0);
  return { rows, qtyTotal };
}

/** Desglose de cada insumo por receta (para modal "Ver receta"). */
export function buildDesgloseInsumoPorReceta(itemsWithQty, recetasFullById, stockCostByProduct, productUnitById = {}) {
  const out = new Map();
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  for (const item of itemsWithQty || []) {
    const idStr = item.receta_plato_id != null ? String(item.receta_plato_id) : null;
    if (!idStr) continue;
    const receta = m.get(idStr);
    if (!receta) continue;
    const rows = lineasInsumoEscaladosRecetaApi(receta, item.qty);
    for (const row of rows) {
      const pid = row.product_id != null ? String(row.product_id) : null;
      const canonicalUnit = pid ? (productUnitById[pid] || row.unit || '') : (row.unit || '');
      const key = pid ? `id:${pid}|||${canonicalUnit}` : `name:${row.product}|||${canonicalUnit}`;
      const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
      const cUnit = Number(cached?.average_cost ?? row.precioUnitario ?? 0);
      const safe = Number.isFinite(cUnit) ? cUnit : 0;
      const subtotal = roundSubtotal4(Number(row.quantity || 0) * safe);
      if (!out.has(key)) out.set(key, []);
      out.get(key).push({
        receta: item.receta_name || receta.name || '—',
        classification: item.classification || '—',
        quantity: Number(row.quantity || 0),
        unit: canonicalUnit || row.unit,
        cUnit: safe,
        subtotal,
      });
    }
  }
  for (const [k, list] of out.entries()) {
    list.sort((a, b) => String(a.receta).localeCompare(String(b.receta), 'es'));
    out.set(k, list);
  }
  return out;
}

export { formatearCantidadReceta4Dec };

/** Costo unitario (kárdex / promedio): 4 decimales para no perder precisión en insumos de bajo costo. */
export function formatearCostoUnitario4Dec(value) {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
  const x = Number.isFinite(n) ? n : 0;
  return x.toLocaleString('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

/** Subtotal de línea: misma precisión que C.unit (4 decimales). */
export const formatearSubtotal4Dec = formatearCostoUnitario4Dec;
